import { useState, useEffect, useCallback } from 'react';
import { fromLonLat, transform } from 'ol/proj';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Text, Fill, Stroke } from 'ol/style';

// Templates for special annotation types (only set defaults, don't override)
const ANNOTATION_TEMPLATES = {
  'View Name': {
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#000000',
    priority: 10,
    declutterPriority: 10,
    offsetY: -15,
    strokeWidth: 1.5,
    strokeColor: '#ffffff'
  },
  'Primary View': {
    // Only set values that should always be enforced
    priority: 11,
    declutterPriority: 11,
    strokeWidth: 1,
    strokeColor: '#ffffff',
    // Default values that can be overridden
    defaultFontSize: 24,
    defaultFontWeight: 'bold',
    defaultFontStyle: 'normal',
    defaultColor: '#333333',
    defaultOffsetY: -20
  }
};

export function useMapAnnotations(viewId, mapInstance, fontVersion = 0) {
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vectorLayer, setVectorLayer] = useState(null);
  const [vectorSource, setVectorSource] = useState(null);

  // Initialize vector layer and source when map instance is available
  useEffect(() => {
    if (!mapInstance) return;

    const source = new VectorSource();
    const layer = new VectorLayer({
      source,
      zIndex: 5000, // Increased from 2000 to 5000 to ensure annotations are above all symbol points
      declutter: true, // Enable decluttering
      renderBuffer: 100, // Increase render buffer for better decluttering
      declutterMode: 'label', // Use 'label' mode to be more lenient with overlapping annotations
      style: (feature) => {
        const annotation = feature.get('annotation');
        if (!annotation) return null;

        // Get template for special types
        const template = ANNOTATION_TEMPLATES[annotation.type];
        
        let fontSize, fontWeight, fontStyle, color, offsetY;
        
        if (annotation.type === 'Primary View') {
          // For Primary View, use custom values if provided, fall back to defaults if not
          fontSize = annotation.font_size || template.defaultFontSize;
          fontWeight = annotation.font_weight || template.defaultFontWeight;
          fontStyle = annotation.font_style || template.defaultFontStyle;
          color = annotation.color || template.defaultColor;
          offsetY = annotation.offsetY || template.defaultOffsetY;
        } else if (annotation.type === 'View Name') {
          // For View Name, use template values
          fontSize = template.fontSize;
          fontWeight = template.fontWeight;
          fontStyle = template.fontStyle;
          color = template.color;
          offsetY = template.offsetY;
        } else {
          // For other types, use annotation values with fallbacks
          fontSize = annotation.font_size || 12;
          fontWeight = annotation.font_weight || 'normal';
          fontStyle = annotation.font_style || 'normal';
          color = annotation.color || '#000000';
          offsetY = annotation.offsetY || -15;
        }

        const strokeWidth = template ? template.strokeWidth : 1.5;
        const strokeColor = template ? template.strokeColor : '#ffffff';
        // Increase priority for all annotations to ensure they're above symbol points
        const priority = template ? template.priority + 100 : (annotation.priority || 0) + 100;
        const declutterPriority = template ? template.declutterPriority + 100 : (annotation.priority || 0) + 100;

        // For View Name and Primary View, convert text to uppercase
        const text = ['View Name', 'Primary View'].includes(annotation.type) 
          ? annotation.text.toUpperCase() 
          : annotation.text;

        // Handle font family based on annotation type and organization settings
        let fontFamily;
        if (annotation.font_family === 'Default') {
          // Use organization fonts: primary for view names, secondary for others
          const isViewName = ['View Name', 'Primary View'].includes(annotation.type);
          const root = document.documentElement;
          if (isViewName) {
            fontFamily = getComputedStyle(root).getPropertyValue('--font-family-heading').trim() ||
                        getComputedStyle(root).getPropertyValue('--org-primary-font-family').trim() ||
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
          } else {
            fontFamily = getComputedStyle(root).getPropertyValue('--font-family-base').trim() ||
                        getComputedStyle(root).getPropertyValue('--org-secondary-font-family').trim() ||
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
          }
        } else {
          // Use custom font family if specified (e.g., water labels override)
          fontFamily = annotation.font_family || 'Arial';
        }

        return new Style({
          text: new Text({
            text: text,
            font: `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`,
            fill: new Fill({
              color: color
            }),
            stroke: new Stroke({
              color: strokeColor,
              width: strokeWidth
            }),
            // Remove offsetY and use anchor instead
            // offsetY: offsetY,
            // Higher priority annotations get higher declutter priority
            declutterOrder: -declutterPriority, // Negative because lower values get rendered first
            padding: [4, 4, 4, 4], // Reduced padding to allow more annotations to be visible
            overflow: true, // Allow text to overflow its render box
            textAlign: 'center',
            textBaseline: 'middle',
            placement: 'point',
            maxAngle: 0,
            rotation: 0,
            // Fix for text anchoring
            anchor: [0.5, 0.5],
            anchorXUnits: 'fraction',
            anchorYUnits: 'fraction',
            // Disable text rotation and scaling
            rotateWithView: false,
            scale: 1,
            // Add offset based on the template or annotation
            offset: [0, offsetY || 0]
          }),
          // Give higher priority annotations a larger declutter box
          declutterBox: {
            width: fontSize * (text.length + 2), // Reduced padding for declutter box
            height: fontSize * 2, // Smaller declutter box
            priority: -declutterPriority // Match text declutter priority
          }
        });
      },
      // Set decluttering options for the layer
      declutterOptions: {
        // Sort features by priority before decluttering
        sortFeatures: (a, b) => {
          const annotationA = a.get('annotation');
          const annotationB = b.get('annotation');
          const priorityA = ANNOTATION_TEMPLATES[annotationA?.type]?.priority || annotationA?.priority || 0;
          const priorityB = ANNOTATION_TEMPLATES[annotationB?.type]?.priority || annotationB?.priority || 0;
          return priorityB - priorityA; // Higher priority first
        },
        // Ensure text is always visible when zooming
        textAlign: 'center',
        textBaseline: 'middle'
      }
    });

    setVectorSource(source);
    setVectorLayer(layer);
    mapInstance.addLayer(layer);

    return () => {
      if (mapInstance) {
        mapInstance.removeLayer(layer);
      }
    };
  }, [mapInstance, fontVersion]); // Add fontVersion to trigger re-render when fonts change

  // Fetch annotations when viewId changes
  useEffect(() => {
    const fetchAnnotations = async () => {
      if (!viewId) return;

      try {
        setLoading(true);
        const response = await fetch(`/api/map-annotations/view/${viewId}`);
        if (!response.ok) throw new Error('Failed to fetch annotations');
        const data = await response.json();
        
        // Sort annotations by priority before setting them
        const sortedAnnotations = data.sort((a, b) => {
          const priorityA = ANNOTATION_TEMPLATES[a.type]?.priority || a.priority || 0;
          const priorityB = ANNOTATION_TEMPLATES[b.type]?.priority || b.priority || 0;
          return priorityB - priorityA;
        });
        
        setAnnotations(sortedAnnotations);
        setError(null);
      } catch (err) {
        console.error('Error fetching annotations:', err);
        setError('Failed to load annotations');
        // Don't clear annotations on error to prevent UI disruption
      } finally {
        setLoading(false);
      }
    };

    fetchAnnotations();
  }, [viewId]);

  // Safe fetch function to handle API requests without causing page reloads
  const safeFetch = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      return await response.json();
    } catch (err) {
      console.error(`Error with API request to ${url}:`, err);
      // Don't throw, return an error object instead
      return { error: err.message };
    }
  }, []);
  
  // Update annotations with optimistic updates
  const updateAnnotation = useCallback(async (annotationId, updates) => {
    // Find the annotation in the current state
    const currentAnnotation = annotations.find(a => a.id === annotationId);
    if (!currentAnnotation) {
      console.error(`Annotation with ID ${annotationId} not found`);
      return false;
    }
    
    // Create updated annotation
    const updatedAnnotation = { ...currentAnnotation, ...updates };
    
    // Optimistically update the UI
    setAnnotations(prev => 
      prev.map(a => a.id === annotationId ? updatedAnnotation : a)
    );
    
    // Send the request to the API
    try {
      const result = await safeFetch(`/api/map-annotations/${annotationId}`, {
        method: 'PUT',
        body: JSON.stringify(updatedAnnotation)
      });
      
      if (result.error) {
        // Revert to the previous state if there was an error
        console.error('Error updating annotation, reverting UI:', result.error);
        setAnnotations(prev => 
          prev.map(a => a.id === annotationId ? currentAnnotation : a)
        );
        return false;
      }
      
      return true;
    } catch (err) {
      // This shouldn't happen with safeFetch, but just in case
      console.error('Unexpected error updating annotation:', err);
      setAnnotations(prev => 
        prev.map(a => a.id === annotationId ? currentAnnotation : a)
      );
      return false;
    }
  }, [annotations, safeFetch]);

  // Update features when annotations change
  useEffect(() => {
    if (!vectorSource) return;

    // Clear existing features
    vectorSource.clear();

    // Add features for each annotation
    annotations.forEach(annotation => {
      // Ensure lat and lon are numbers
      const lat = typeof annotation.lat === 'string' ? parseFloat(annotation.lat) : annotation.lat;
      const lon = typeof annotation.lon === 'string' ? parseFloat(annotation.lon) : annotation.lon;

      if (isNaN(lat) || isNaN(lon)) {
        console.error('Invalid coordinates for annotation:', annotation);
        return;
      }

      const feature = new Feature({
        geometry: new Point(fromLonLat([lon, lat])),
        annotation: {
          ...annotation,
          // Ensure priority is a number and at least 1 (higher than symbol points)
          priority: Math.max(1, ANNOTATION_TEMPLATES[annotation.type]?.priority || parseInt(annotation.priority) || 1)
        }
      });

      feature.setId(annotation.id);
      vectorSource.addFeature(feature);
    });

    // Force a redraw of the vector layer
    if (vectorLayer) {
      vectorLayer.changed();
      if (mapInstance) {
        mapInstance.render();
      }
    }
  }, [annotations, vectorSource, vectorLayer, mapInstance]);

  return {
    annotations,
    loading,
    error,
    vectorLayer,
    vectorSource,
    updateAnnotation // Export the updateAnnotation function
  };
}

export default useMapAnnotations; 